import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell, PanImage } from "../components/PageShell";

export const Route = createFileRoute("/monday-nights")({
  head: () => ({
    meta: [
      { title: "Monday Nights! in the Park — The Pan Institute" },
      {
        name: "description",
        content:
          "Monday Nights street dances and steel band performances in Blue Hill, Maine.",
      },
      { property: "og:title", content: "Monday Nights! in the Park" },
      {
        property: "og:description",
        content:
          "Monday Nights street dances and steel band performances in Blue Hill, Maine.",
      },
    ],
  }),
  component: MondayNightsPage,
});

const FACTS = [
  { k: "7:30 p.m.", v: "Show time" },
  { k: "Donate", v: "What you can" },
];

const STORY = [
  {
    year: "1975",
    title: "On the post office steps",
    body: "Carl Chase starts Monday Nights as a street dance on the steps of the South Brooksville Post Office. Atlantic Clarion Steel Band plays for the local schooner passengers, back-to-the-land farmers, and locals who would come to dance in the street or listen across the street under the trees on a neighbor's lawn.",
  },
  {
    year: "1979",
    title: "Shut down",
    body: "Some decided that squealing their tires would enhance the setting — which lead to neighbors complaining and the festivities were shut down.",
  },
  {
    year: "1990s",
    title: "Back behind the market",
    body: "Carl, his son Nigel Chase, and Butch Czerwinski started classes for the public behind the Buck Harbor Market. This lead to the return of the Monday Night street dances, now around the corner in front of the market.",
  },
  {
    year: "Today",
    title: "Blue Hill town park",
    body: "Atlantic Clarion Steel Band continued to play but soon joined with the players from the beginner classes to form a band — which after a few different name changes is still known today as Flash in the Pans. But again neighborly disenchantment and large crowds forced the band to move to Blue Hill, where it still performs in the town park during summer, every other Monday.",
  },
];

function MondayNightsPage() {
  return (
    <PageShell
      tag=""
      title={<>Monday Nights! <span className="text-primary">in the Park</span>.</>}
      intro="Monday Nights steelband performances were started in 1975 by Carl Chase on the steps of the South Brooksville Post Office. A Maine summer ritual that still fills the park every other Monday."
    >
      {/* Hero photo with overlay */}
      <div className="relative overflow-hidden rounded-3xl border border-border/40">
        <div className="aspect-[16/10]">
          <PanImage id="Monday%20Nights!-0001.jpg" alt="Monday Nights street dance in Blue Hill" width={1800} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/55 to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
          <div className="text-[10px] uppercase tracking-[0.5em] text-primary">Blue Hill, Maine · Town Park</div>
          <p className="mt-4 font-display italic text-2xl md:text-4xl leading-[1.1] max-w-3xl">
            Steel pans in the park. Dancing in the street. Every other Monday, all summer.
          </p>
        </div>
      </div>

      {/* Facts strip */}
      <dl className="mt-10 grid grid-cols-2 divide-x divide-border/40 rounded-2xl border border-border/40 bg-card/30">
        {FACTS.map((s) => (
          <div key={s.k} className="px-4 py-6 text-center">
            <dt className="font-display italic text-2xl md:text-4xl text-primary leading-none">{s.k}</dt>
            <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">{s.v}</dd>
          </div>
        ))}
      </dl>

      {/* Story timeline */}
      <div className="mt-24">
        <div className="flex items-end justify-between gap-6 mb-10 border-b border-border/40 pb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">The story</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">A 50-year street dance</h2>
          </div>
        </div>
        <ol className="relative border-l border-border/50 ml-3 space-y-12">
          {STORY.map((t, i) => (
            <li key={i} className="group pl-8 relative">
              <span className="absolute -left-[7px] top-2 h-3 w-3 rounded-full bg-primary/70 border-2 border-background group-hover:scale-125 transition" />
              <div className="text-[10px] uppercase tracking-[0.4em] text-primary">{t.year}</div>
              <h3 className="mt-2 font-display italic text-2xl">{t.title}</h3>
              <p className="mt-3 text-[14px] text-foreground/70 leading-relaxed max-w-2xl">{t.body}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Flash in the Pans affiliate */}
      <div className="mt-24">
        <div className="flex items-end justify-between gap-6 mb-10 border-b border-border/40 pb-5">
          <div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl">Flash! in the Pans</h2>
          </div>
        </div>
        <div className="aspect-video overflow-hidden rounded-3xl border border-border/40 bg-card/20">
          <iframe
            className="h-full w-full"
            src="https://www.youtube.com/embed/Z26UROeIs-o?start=2"
            title="Flash! in the Pans"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        <p className="mt-5 text-sm text-foreground/65">
          <a
            href="https://www.flashinthepans.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/70 transition underline underline-offset-4"
          >
            Visit the Flash! in the Pans website
          </a>
        </p>
      </div>

      <div className="mt-24 rounded-3xl border border-border/40 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Don't miss a Monday</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              See when the next street dance lands.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/calendar"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/10 whitespace-nowrap"
            >
              See the calendar
            </Link>
          </div>
        </div>
      </div>

    </PageShell>
  );
}
